import {
  IonButton,
  IonContent,
  IonIcon,
  IonImg,
  IonMenuToggle,
  IonPage,
  useIonAlert,
  useIonToast,
} from '@ionic/react';
import {
  chevronDownOutline,
  chevronForwardOutline,
  ellipsisVertical,
  refreshCircleOutline,
  searchOutline,
} from 'ionicons/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { firstValueFrom } from 'rxjs';
import { IState } from 'appRedux/createStore';
import {
  CasinoService,
  IGameNameComboRow,
  IGameNamesManagementResponse,
  ISetSlotcheckGameNamesResponse,
  IUnsetSlotcheckGameNamesResponse,
} from 'services/casino';
import {
  GAME_NAMES_MANAGEMENT_GROUP,
  canAccessGameNamesManagement,
} from './gameNamesManagementAccess';
import {
  addRecentSlotcheckValues,
  buildGameNamesListQuery,
  collectSlotcheckAutocomplete,
  comboRowKey,
  defaultDraftForRow,
  filterSortGameNameGroups,
  filterSortGameNameRows,
  gameNameGroupKey,
  isMissingSlotcheckName,
  isMissingSlotcheckTitle,
  managedGroupsFromPayload,
  manufacturerFilterOptions,
  payloadSlotcheckFields,
  pruneExpandedGroups,
  rawManufacturerCodesLabel,
  setGameNamesConfirmMessage,
  SortKey,
  toggleExpandedGroup,
  unmapGameNameConfirmMessage,
  unmapGameNamesBody,
  unmapRowKey,
} from './gameNamesManagementUi';
import { refreshKeepingIonScroll } from '../ManufacturersManagement/egmsAdminAfterSet';
import { buildAdminConfirmAlertOptions } from 'components/AdminConfirmAlert/adminConfirmAlert';

type TabId = 'managed' | 'unmapped';

const PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 350;

const MenuKebab = () => (
  <div className="game-names-mgmt__menu-fab">
    <IonMenuToggle menu="main">
      <IonButton fill="clear" className="game-names-mgmt__menu-btn" aria-label="Open menu">
        <IonIcon icon={ellipsisVertical} className="game-names-mgmt__menu-icon" />
      </IonButton>
    </IonMenuToggle>
  </div>
);

const ForbiddenCard = () => (
  <IonPage className="app-parent game-names-mgmt">
    <IonContent className="game-names-mgmt">
      <MenuKebab />
      <div className="game-names-mgmt__content">
        <div className="game-names-mgmt__breadcrumb-row">
          <IonMenuToggle menu="main">
            <IonButton fill="clear" className="game-names-mgmt__back" aria-label="Open menu">
              <IonImg src="assets/images/back-icon.svg" />
            </IonButton>
          </IonMenuToggle>
          <div className="game-names-mgmt__breadcrumb">
            <span>Game Names Management</span>
          </div>
        </div>
        <div className="game-names-mgmt__forbidden-card">
          <h2>403 · Game Names Management not enabled</h2>
          <p>
            Requires Cognito group <strong>{GAME_NAMES_MANAGEMENT_GROUP}</strong>. Ask ops to create
            and assign it in the Cognito user pool (dev and prod{' '}
            <code>us-east-1_wixKjr4v5</code>).
          </p>
        </div>
      </div>
    </IonContent>
  </IonPage>
);

type DraftPair = { slotcheckGameName: string; slotcheckGameTitle: string };

export const GameNamesManagementPage = () => {
  const casinoSvc = useMemo(() => new CasinoService(), []);
  const [presentToast] = useIonToast();
  const [presentAlert] = useIonAlert();
  const groups =
    (useSelector<IState>((s) => s.app.persistedState.userInfo?.groups) as string[]) || [];
  const canAccess = canAccessGameNamesManagement(groups);

  const [tab, setTab] = useState<TabId>('unmapped');
  const [phase, setPhase] = useState<'idle' | 'loading' | 'ready'>('idle');
  const [refreshBusy, setRefreshBusy] = useState(false);
  const [payload, setPayload] = useState<IGameNamesManagementResponse | null>(null);
  const [queryInput, setQueryInput] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [mfrFilter, setMfrFilter] = useState('');
  const [debouncedMfr, setDebouncedMfr] = useState('');
  /** Active EGMs only: hide combos with no active EGM (server + instant). Default off. */
  const [activeOnly, setActiveOnly] = useState(false);
  /** Filters that produced the current `payload` (updated only after a successful load). */
  const [appliedServerFilters, setAppliedServerFilters] = useState({
    q: '',
    mfr: '',
    activeOnly: false,
  });
  /** SlotCheck values saved with Set on this screen — autocomplete options right away. */
  const [recentSlotcheck, setRecentSlotcheck] = useState<{ names: string[]; titles: string[] }>(
    () => ({ names: [], titles: [] })
  );
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [offset, setOffset] = useState(0);
  const [draftByKey, setDraftByKey] = useState<Record<string, DraftPair>>({});
  const [busyKey, setBusyKey] = useState<string | null>(null);
  /** Multi-open accordion keys = SlotCheck (Game Name, Game Theme) pairs. */
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set());
  const [busyUnmapKey, setBusyUnmapKey] = useState<string | null>(null);
  const loadSeq = useRef(0);
  const hasPayloadRef = useRef(false);
  const contentRef = useRef<HTMLIonContentElement | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(queryInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [queryInput]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedMfr(mfrFilter.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [mfrFilter]);

  useEffect(() => {
    setOffset(0);
  }, [debouncedQ, debouncedMfr, activeOnly]);

  const load = useCallback(
    async (opts?: { fromRefresh?: boolean; offset?: number }) => {
      if (!canAccess) return;
      const seq = ++loadSeq.current;
      const nextOffset = opts?.offset ?? 0;
      const requestQ = debouncedQ;
      const requestMfr = debouncedMfr;
      const requestActiveOnly = activeOnly;
      if (opts?.fromRefresh) setRefreshBusy(true);
      // Keep on-screen rows while a follow-on server filter runs (instant client filter stays visible).
      else if (!hasPayloadRef.current) setPhase('loading');
      try {
        const res = await firstValueFrom(
          casinoSvc.GetGameNamesManagement(
            buildGameNamesListQuery({
              q: requestQ,
              manufacturer: requestMfr,
              activeOnly: requestActiveOnly,
              limit: PAGE_SIZE,
              offset: nextOffset,
            })
          )
        );
        if (seq !== loadSeq.current) return;
        setPayload(res && typeof res === 'object' ? res : null);
        hasPayloadRef.current = Boolean(res && typeof res === 'object');
        setAppliedServerFilters({ q: requestQ, mfr: requestMfr, activeOnly: requestActiveOnly });
        setOffset(nextOffset);
        setPhase('ready');
      } catch (err: unknown) {
        if (seq !== loadSeq.current) return;
        setPayload(null);
        hasPayloadRef.current = false;
        setPhase('ready');
        const status =
          (err as { response?: { status?: number }; status?: number })?.response?.status ||
          (err as { status?: number })?.status;
        if (status !== 403) {
          void presentToast({
            position: 'top',
            duration: 4000,
            color: 'danger',
            message: 'Failed to load Game Names Management.',
          });
        }
      } finally {
        if (seq === loadSeq.current) setRefreshBusy(false);
      }
    },
    [canAccess, casinoSvc, debouncedQ, debouncedMfr, activeOnly, presentToast]
  );

  useEffect(() => {
    void load({ offset: 0 });
  }, [load]);

  // Instant as-you-type filter of the loaded page; server result replaces when debounce completes.
  // SlotCheck tab = accordion groups (SlotCheck pair → raw combos mapped into it).
  const payloadGroups = useMemo(() => managedGroupsFromPayload(payload), [payload]);
  const managedGroups = useMemo(
    () =>
      filterSortGameNameGroups(payloadGroups, queryInput, mfrFilter, sortKey, sortDir, activeOnly),
    [payloadGroups, queryInput, mfrFilter, sortKey, sortDir, activeOnly]
  );

  // Drop expanded keys for groups that disappeared after Unmap / refresh.
  useEffect(() => {
    setExpandedGroups((prev) => pruneExpandedGroups(prev, payloadGroups));
  }, [payloadGroups]);
  const unmappedRows = useMemo(
    () =>
      filterSortGameNameRows(
        payload?.unmapped || [],
        queryInput,
        mfrFilter,
        sortKey,
        sortDir,
        activeOnly
      ),
    [payload, queryInput, mfrFilter, sortKey, sortDir, activeOnly]
  );

  // SlotCheck Game Name / Theme options: the full server list (every SlotCheck value
  // in Mongo machines — not just this page / search), plus values saved on this screen.
  const autocomplete = useMemo(
    () =>
      collectSlotcheckAutocomplete(
        [...(payload?.managed || []), ...payloadGroups.flatMap((g) => g.mappedFrom || [])],
        [
          { names: payload?.slotcheckGameNames, titles: payload?.slotcheckGameTitles },
          recentSlotcheck,
        ]
      ),
    [payload, payloadGroups, recentSlotcheck]
  );
  const manufacturerOptions = useMemo(
    () => manufacturerFilterOptions(payload?.manufacturers),
    [payload]
  );

  const getDraft = (row: IGameNameComboRow): DraftPair => {
    const key = comboRowKey(row);
    return draftByKey[key] || defaultDraftForRow(row);
  };

  const setDraftField = (
    row: IGameNameComboRow,
    field: 'slotcheckGameName' | 'slotcheckGameTitle',
    value: string
  ) => {
    const key = comboRowKey(row);
    setDraftByKey((prev) => {
      const base = prev[key] || defaultDraftForRow(row);
      return { ...prev, [key]: { ...base, [field]: value } };
    });
  };

  const confirmSet = (row: IGameNameComboRow) => {
    const draft = getDraft(row);
    const payloadFields = payloadSlotcheckFields(
      row,
      draft.slotcheckGameName,
      draft.slotcheckGameTitle
    );
    if (!payloadFields.slotcheckGameName && !payloadFields.slotcheckGameTitle) {
      void presentToast({
        position: 'top',
        duration: 3000,
        color: 'warning',
        message: 'Enter SlotCheck Game Name and/or Game Theme for blank fields.',
      });
      return;
    }
    const key = comboRowKey(row);
    void presentAlert(
      buildAdminConfirmAlertOptions({
        header: 'Set SlotCheck Game Name / Theme',
        message: setGameNamesConfirmMessage(
          row,
          payloadFields.slotcheckGameName,
          payloadFields.slotcheckGameTitle
        ),
        onConfirm: () => {
          void (async () => {
            setBusyKey(key);
            try {
              const res = (await firstValueFrom(
                casinoSvc.SetSlotcheckGameNames({
                  gameName: row.gameName,
                  gameTitle: row.gameTitle,
                  manufacturer: row.manufacturer,
                  slotcheckGameName: payloadFields.slotcheckGameName,
                  slotcheckGameTitle: payloadFields.slotcheckGameTitle,
                })
              )) as ISetSlotcheckGameNamesResponse;
              void presentToast({
                position: 'top',
                duration: 4000,
                color: 'success',
                message: `Updated ${res?.modifiedCount ?? 0} of ${
                  res?.matchedCount ?? 0
                } matched EGMs.`,
              });
              // New names are options at once (the refresh below also returns them).
              setRecentSlotcheck((prev) =>
                addRecentSlotcheckValues(
                  prev,
                  payloadFields.slotcheckGameName,
                  payloadFields.slotcheckGameTitle
                )
              );
              setDraftByKey((prev) => {
                const next = { ...prev };
                delete next[key];
                return next;
              });
              // Stay on current tab; keep search/manufacturer filter/sort; refresh
              // list in place (toast only — never auto-switch to SlotCheck tab).
              await refreshKeepingIonScroll(contentRef.current, () =>
                load({ fromRefresh: true, offset })
              );
            } catch {
              void presentToast({
                position: 'top',
                duration: 4000,
                color: 'danger',
                message: 'Update failed.',
              });
            } finally {
              setBusyKey(null);
            }
          })();
        },
      })
    );
  };

  const confirmUnmap = (row: IGameNameComboRow) => {
    const unmapKey = unmapRowKey(row);
    void presentAlert(
      buildAdminConfirmAlertOptions({
        header: 'Unmap game name',
        message: unmapGameNameConfirmMessage(row),
        confirmText: 'Unmap',
        onConfirm: () => {
          void (async () => {
            setBusyUnmapKey(unmapKey);
            try {
              const res = (await firstValueFrom(
                casinoSvc.UnsetSlotcheckGameNames(unmapGameNamesBody(row))
              )) as IUnsetSlotcheckGameNamesResponse;
              void presentToast({
                position: 'top',
                duration: 4000,
                color: 'success',
                message: `Unmapped ${res?.modifiedCount ?? 0} of ${
                  res?.matchedCount ?? 0
                } matched EGMs.`,
              });
              // Stay on SlotCheck Game Names; keep search/manufacturer filter/sort/expanded;
              // refresh lists in place so counts update and the combo shows in Unmapped.
              await refreshKeepingIonScroll(contentRef.current, () =>
                load({ fromRefresh: true, offset })
              );
            } catch {
              void presentToast({
                position: 'top',
                duration: 4000,
                color: 'danger',
                message: 'Unmap failed.',
              });
            } finally {
              setBusyUnmapKey(null);
            }
          })();
        },
      })
    );
  };

  if (!canAccess) return <ForbiddenCard />;

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  };

  const inputMatchesServer =
    queryInput.trim() === appliedServerFilters.q &&
    mfrFilter.trim() === appliedServerFilters.mfr &&
    activeOnly === appliedServerFilters.activeOnly;
  // While typing, counts follow the instant client-filtered page; after server catches up, use totals.
  // SlotCheck tab counts SlotCheck pairs (groups), like Manufacturers Management.
  const managedTotal = inputMatchesServer
    ? payload?.managedGroupsTotal ?? managedGroups.length
    : managedGroups.length;
  const unmappedTotal = inputMatchesServer
    ? payload?.unmappedTotal ?? unmappedRows.length
    : unmappedRows.length;
  const activeTotal = tab === 'managed' ? managedTotal : unmappedTotal;
  const serverFilterPending =
    !inputMatchesServer ||
    debouncedQ !== appliedServerFilters.q ||
    debouncedMfr !== appliedServerFilters.mfr;
  const canPrev = inputMatchesServer && offset > 0;
  const canNext = inputMatchesServer && offset + PAGE_SIZE < activeTotal;

  const renderSetControls = (row: IGameNameComboRow, opts?: { partial?: boolean }) => {
    const key = comboRowKey(row);
    const draft = getDraft(row);
    const nameLocked = opts?.partial && !isMissingSlotcheckName(row);
    const titleLocked = opts?.partial && !isMissingSlotcheckTitle(row);
    return (
      <div className="game-names-mgmt__set">
        <label className="game-names-mgmt__field">
          <span
            className={
              opts?.partial && isMissingSlotcheckName(row)
                ? 'game-names-mgmt__field-label game-names-mgmt__field-label--missing'
                : 'game-names-mgmt__field-label'
            }
          >
            Game Name (SlotCheck)
          </span>
          <input
            className={
              opts?.partial && isMissingSlotcheckName(row)
                ? 'game-names-mgmt__input game-names-mgmt__input--missing'
                : 'game-names-mgmt__input'
            }
            list="gnm-sc-names"
            placeholder="SlotCheck Game Name…"
            value={draft.slotcheckGameName}
            onChange={(e) => setDraftField(row, 'slotcheckGameName', e.target.value)}
            disabled={busyKey === key || nameLocked}
          />
        </label>
        <label className="game-names-mgmt__field">
          <span
            className={
              opts?.partial && isMissingSlotcheckTitle(row)
                ? 'game-names-mgmt__field-label game-names-mgmt__field-label--missing'
                : 'game-names-mgmt__field-label'
            }
          >
            Game Theme (SlotCheck)
          </span>
          <input
            className={
              opts?.partial && isMissingSlotcheckTitle(row)
                ? 'game-names-mgmt__input game-names-mgmt__input--missing'
                : 'game-names-mgmt__input'
            }
            list="gnm-sc-titles"
            placeholder="SlotCheck Game Theme…"
            value={draft.slotcheckGameTitle}
            onChange={(e) => setDraftField(row, 'slotcheckGameTitle', e.target.value)}
            disabled={busyKey === key || titleLocked}
          />
        </label>
        <IonButton
          size="small"
          fill="solid"
          disabled={busyKey === key}
          onClick={() => confirmSet(row)}
        >
          {busyKey === key ? 'Updating…' : 'Set'}
        </IonButton>
      </div>
    );
  };

  return (
    <IonPage className="app-parent game-names-mgmt">
      <IonContent ref={contentRef} className="game-names-mgmt">
        <MenuKebab />
        <div className="game-names-mgmt__content">
          <div className="game-names-mgmt__breadcrumb-row">
            <IonMenuToggle menu="main">
              <IonButton fill="clear" className="game-names-mgmt__back" aria-label="Open menu">
                <IonImg src="assets/images/back-icon.svg" />
              </IonButton>
            </IonMenuToggle>
            <div className="game-names-mgmt__breadcrumb">
              <span>Game Names Management</span>
            </div>
          </div>

          <div className="game-names-mgmt__header">
            <div className="game-names-mgmt__title-row">
              <h1 className="game-names-mgmt__title">Game Names Management</h1>
              <IonButton
                className="game-names-mgmt__refresh"
                fill="solid"
                disabled={refreshBusy || (phase === 'loading' && !payload)}
                onClick={() => void load({ fromRefresh: true, offset: 0 })}
              >
                <IonIcon icon={refreshCircleOutline} slot="start" />
                {refreshBusy ? 'Refreshing…' : 'Refresh'}
              </IonButton>
            </div>
            <p className="game-names-mgmt__subtitle">
              Combos of Game Name + Game Theme (+ manufacturer) from insight <code>machines</code>.
              Only <code>slotcheckGameName</code> / <code>slotcheckGameTitle</code> are written —
              raw <code>gameName</code>, <code>gameTitle</code>, and <code>manufacturer</code> are
              never changed. Partials (one SlotCheck field set) appear in the SlotCheck list with
              the missing field highlighted.
            </p>
          </div>

          <div className="game-names-mgmt__tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'managed'}
              className={
                tab === 'managed'
                  ? 'game-names-mgmt__tab game-names-mgmt__tab--active'
                  : 'game-names-mgmt__tab'
              }
              onClick={() => setTab('managed')}
            >
              SlotCheck Game Names
              <span className="game-names-mgmt__tab-count">{managedTotal}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'unmapped'}
              className={
                tab === 'unmapped'
                  ? 'game-names-mgmt__tab game-names-mgmt__tab--active'
                  : 'game-names-mgmt__tab'
              }
              onClick={() => setTab('unmapped')}
            >
              Unmapped (raw machine data)
              <span className="game-names-mgmt__tab-count">{unmappedTotal}</span>
            </button>
          </div>

          <div className="game-names-mgmt__toolbar">
            <div className="game-names-mgmt__search">
              <IonIcon icon={searchOutline} />
              <input
                className="game-names-mgmt__filter-input"
                type="search"
                value={queryInput}
                placeholder="Search Game Name / Theme (raw + SlotCheck)…"
                aria-label="Search Game Name or Theme"
                autoComplete="off"
                onChange={(e) => setQueryInput(e.target.value)}
              />
            </div>
            <div className="game-names-mgmt__search game-names-mgmt__search--mfr">
              <input
                className="game-names-mgmt__filter-input"
                type="text"
                list="gnm-manufacturer-options"
                value={mfrFilter}
                placeholder="Filter manufacturer…"
                aria-label="Filter manufacturer"
                autoComplete="off"
                onChange={(e) => setMfrFilter(e.target.value)}
              />
            </div>
            <label
              className={
                activeOnly
                  ? 'game-names-mgmt__active-toggle game-names-mgmt__active-toggle--on'
                  : 'game-names-mgmt__active-toggle'
              }
            >
              <input
                type="checkbox"
                checked={activeOnly}
                aria-label="Active EGMs only"
                data-testid="active-only-toggle"
                onChange={(e) => setActiveOnly(e.target.checked)}
              />
              <span>Active EGMs only</span>
            </label>
            <div className="game-names-mgmt__sort">
              <button type="button" onClick={() => toggleSort('name')}>
                Name{sortKey === 'name' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
              </button>
              <button type="button" onClick={() => toggleSort('egmCount')}>
                EGMs{sortKey === 'egmCount' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
              </button>
              <button type="button" onClick={() => toggleSort('activeCount')}>
                Active{sortKey === 'activeCount' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
              </button>
            </div>
          </div>

          {phase === 'loading' && !payload ? (
            <p className="game-names-mgmt__loading">Loading…</p>
          ) : tab === 'managed' ? (
            <section className="game-names-mgmt__card" data-testid="managed-list">
              <header className="game-names-mgmt__section-head">
                <h2>SlotCheck values</h2>
                <p>
                  Each row is a SlotCheck Game Name / Game Theme. Tap a row to see the raw Game
                  Name / Game Theme (+ manufacturer) combos mapped into it, and Unmap any of them.
                  Partials are editable for the missing SlotCheck field inside the row.
                </p>
              </header>
              {managedGroups.length === 0 ? (
                <p className="game-names-mgmt__empty">
                  {activeOnly
                    ? 'No SlotCheck game name combos with active EGMs.'
                    : 'No SlotCheck game name combos.'}
                </p>
              ) : (
                <ul className="game-names-mgmt__list">
                  {managedGroups.map((group) => {
                    const groupKey = gameNameGroupKey(group);
                    const open = expandedGroups.has(groupKey);
                    const mapped = group.mappedFrom || [];
                    const missingName = !group.slotcheckGameName;
                    const missingTitle = !group.slotcheckGameTitle;
                    const rowClass = [
                      'game-names-mgmt__row',
                      'game-names-mgmt__row--expandable',
                      open ? 'game-names-mgmt__row--open' : '',
                      group.isPartial ? 'game-names-mgmt__row--partial' : '',
                    ]
                      .filter(Boolean)
                      .join(' ');
                    return (
                      <li key={groupKey} className={rowClass}>
                        <button
                          type="button"
                          className="game-names-mgmt__row-toggle"
                          aria-expanded={open}
                          onClick={() =>
                            setExpandedGroups((prev) => toggleExpandedGroup(prev, groupKey))
                          }
                        >
                          <IonIcon
                            icon={open ? chevronDownOutline : chevronForwardOutline}
                            className="game-names-mgmt__chevron"
                            aria-hidden="true"
                          />
                          <div className="game-names-mgmt__row-main">
                            <div className="game-names-mgmt__name">
                              <span
                                className={
                                  missingName
                                    ? 'game-names-mgmt__value game-names-mgmt__value--missing'
                                    : 'game-names-mgmt__value'
                                }
                              >
                                {group.slotcheckGameName || '—'}
                              </span>
                              <span className="game-names-mgmt__sep">·</span>
                              <span
                                className={
                                  missingTitle
                                    ? 'game-names-mgmt__value game-names-mgmt__value--missing'
                                    : 'game-names-mgmt__value'
                                }
                              >
                                {group.slotcheckGameTitle || '—'}
                              </span>
                              {group.isPartial ? (
                                <span className="game-names-mgmt__badge">Partial</span>
                              ) : null}
                            </div>
                            <div className="game-names-mgmt__mapped">
                              Maps from {mapped.length.toLocaleString()} raw combo
                              {mapped.length === 1 ? '' : 's'}
                            </div>
                          </div>
                          <div className="game-names-mgmt__counts">
                            <span>
                              <strong>{group.egmCount.toLocaleString()}</strong> EGMs
                            </span>
                            <span className="game-names-mgmt__muted">
                              {group.activeCount.toLocaleString()} active
                            </span>
                          </div>
                        </button>
                        {open ? (
                          <ul className="game-names-mgmt__sublist" data-testid="mapped-raw-list">
                            {mapped.map((row) => {
                              const unmapKey = unmapRowKey(row);
                              return (
                                <li key={comboRowKey(row)} className="game-names-mgmt__subrow">
                                  <div className="game-names-mgmt__subrow-head">
                                    <div className="game-names-mgmt__subrow-main">
                                      <div className="game-names-mgmt__subname">
                                        {row.gameName || '—'}
                                        <span className="game-names-mgmt__sep"> · </span>
                                        {row.gameTitle || '—'}
                                      </div>
                                      <div className="game-names-mgmt__mapped">
                                        Manufacturer:{' '}
                                        {row.displayManufacturer || row.manufacturer || '—'}
                                      </div>
                                      {rawManufacturerCodesLabel(row) ? (
                                        <div
                                          className="game-names-mgmt__mapped game-names-mgmt__muted"
                                          data-testid="raw-manufacturer-codes"
                                        >
                                          {rawManufacturerCodesLabel(row)}
                                        </div>
                                      ) : null}
                                      <div className="game-names-mgmt__counts game-names-mgmt__counts--inline">
                                        <span>
                                          <strong>{row.egmCount.toLocaleString()}</strong> EGMs
                                        </span>
                                        <span className="game-names-mgmt__muted">
                                          {row.activeCount.toLocaleString()} active
                                        </span>
                                      </div>
                                    </div>
                                    <IonButton
                                      size="small"
                                      fill="outline"
                                      className="game-names-mgmt__unmap"
                                      disabled={busyUnmapKey === unmapKey}
                                      onClick={() => confirmUnmap(row)}
                                    >
                                      {busyUnmapKey === unmapKey ? 'Unmapping…' : 'Unmap'}
                                    </IonButton>
                                  </div>
                                  {row.isPartial || group.isPartial
                                    ? renderSetControls(row, { partial: true })
                                    : null}
                                </li>
                              );
                            })}
                          </ul>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          ) : (
            <section className="game-names-mgmt__card" data-testid="unmapped-list">
              <header className="game-names-mgmt__section-head">
                <h2>Raw machine data (unmapped)</h2>
                <p>
                  Both SlotCheck fields blank. Columns: Game Name, Game Theme, Manufacturer, EGMs.
                  Set writes only <code>slotcheckGameName</code> / <code>slotcheckGameTitle</code>.
                </p>
              </header>
              {unmappedRows.length === 0 ? (
                <p className="game-names-mgmt__empty">
                  {activeOnly
                    ? 'No unmapped game name combos with active EGMs.'
                    : 'No unmapped game name combos.'}
                </p>
              ) : (
                <ul className="game-names-mgmt__list">
                  {unmappedRows.map((row) => {
                    const key = comboRowKey(row);
                    return (
                      <li
                        key={key}
                        className="game-names-mgmt__row game-names-mgmt__row--action"
                      >
                        <div className="game-names-mgmt__row-main">
                          <div className="game-names-mgmt__name">
                            {row.gameName || '—'}
                            <span className="game-names-mgmt__sep">·</span>
                            {row.gameTitle || '—'}
                          </div>
                          <div className="game-names-mgmt__mapped">
                            Manufacturer: {row.displayManufacturer || row.manufacturer || '—'}
                          </div>
                          {rawManufacturerCodesLabel(row) ? (
                            <div
                              className="game-names-mgmt__mapped game-names-mgmt__muted"
                              data-testid="raw-manufacturer-codes"
                            >
                              {rawManufacturerCodesLabel(row)}
                            </div>
                          ) : null}
                          <div className="game-names-mgmt__counts game-names-mgmt__counts--inline">
                            <span>
                              <strong>{row.egmCount.toLocaleString()}</strong> EGMs
                            </span>
                            <span className="game-names-mgmt__muted">
                              {row.activeCount.toLocaleString()} active
                            </span>
                          </div>
                        </div>
                        {renderSetControls(row)}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          )}

          {payload ? (
            <div className="game-names-mgmt__pager">
              <span>
                {serverFilterPending
                  ? `${activeTotal.toLocaleString()} on screen` +
                    (queryInput.trim() || mfrFilter.trim() || activeOnly ? ' (filtering…)' : '')
                  : `Showing ${activeTotal === 0 ? 0 : offset + 1}–${Math.min(
                      offset + PAGE_SIZE,
                      activeTotal
                    )} of ${activeTotal}`}
              </span>
              <div className="game-names-mgmt__pager-btns">
                <IonButton
                  size="small"
                  fill="outline"
                  disabled={!canPrev}
                  onClick={() => void load({ offset: Math.max(0, offset - PAGE_SIZE) })}
                >
                  Previous
                </IonButton>
                <IonButton
                  size="small"
                  fill="outline"
                  disabled={!canNext}
                  onClick={() => void load({ offset: offset + PAGE_SIZE })}
                >
                  Next
                </IonButton>
              </div>
            </div>
          ) : null}

          <datalist id="gnm-manufacturer-options">
            {manufacturerOptions.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
          <datalist id="gnm-sc-names">
            {autocomplete.names.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
          <datalist id="gnm-sc-titles">
            {autocomplete.titles.map((title) => (
              <option key={title} value={title} />
            ))}
          </datalist>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default GameNamesManagementPage;
